import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { AzureSearchRequest, AzureSearchService } from "../../../azure-search/azure-search";
import { ATTACHMENTS_PRIMARY_TYPE, ATTACHMENT_MACHINE_TYPES, COMMERCE_DEPARTMENTS, commerceDepartment, InventoryItem } from "../../../inventory/inventory";
import { catchError, filter, forkJoin, map, of, Subscription } from "rxjs";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { LocalStorageService } from "../../../core/core";
import { HeaderService } from "../header.service";
import { headerMenuBounds } from "../dropdown-position";
import { HEADER_LOCATIONS, headerLocationRoute, isCommerceNavigationUrl } from "../header-nav-data";
import { ANYWHERE_SEARCH_RADIUS_KM } from "../../../search-results/models/facets/location-facet.model";
import { HttpClient, HttpParams } from "@angular/common/http";
import { EnvironmentConfig, isStorefrontEnabled, liveAttachmentMachineTypes } from "../../../core/core";

declare const environment: EnvironmentConfig;

interface AttachmentMenuCategory { departmentHandle?: string; name: string; handle: string; count: number; }

// CSS class added to <body> when the mobile chip row is visible so the global
// `.market-body` margin can grow to clear the taller navbar (~100px) and stop
// covering breadcrumbs / page content.
const BODY_CLASS_WITH_CHIPS = "market-body--with-mobile-chips";

interface LocationState {
  state: string;
  stateAbbr: string;
  cities: string[];
}

@Component({
  selector: "common-header-left-side",
  templateUrl: "./header-left-side.component.html",
  styleUrls: ["./header-left-side.component.scss"],
})
export class HeaderLeftSideComponent implements OnInit, OnDestroy {
  baseFilter: string =
    "(status eq 'Available' or status eq 'Onboarding') and " +
    "(rentalRate ne 0) and " +
    "(requiresAdminApproval eq false) and " + // we can get rid of this in a few days
    "(approvalStatus eq 'Approved' or approvalStatus eq null)";

  renterNavigationItem: any[] = [
    {
      label: "All Rentals",
      title: "Popular Rental Categories",
      queryParams: { radius: "anywhere" },
      route: "equipment-rentals",
    },
    {
      label: "Buy it Now",
      title: "Popular Buy it Now Categories",
      filter: " and (buyItNowEnabled eq true)",
      queryParams: { buyItNowEnabled: true, radius: "anywhere" },
      route: "equipment-for-sale",
    },
    {
      label: "Rent to Own",
      title: "Popular Rent to Own Categories",
      filter: " and (rpoEnabled eq true)",
      queryParams: { rpoEnabled: true, radius: "anywhere" },
      route: "equipment-rentals",
    },
  ];

  selectedTitle: string;
  queryParams: any;
  popularMachineCategoryData: any[];
  title: any;
  subscription: Subscription;
  private mobileDropdownsSubscription: Subscription;
  private menuPositionFrame: number;
  private menuCloseTimer: ReturnType<typeof setTimeout>;
  private readonly viewportResize = () => this.scheduleMenuPosition();
  selectedRoute: string = "equipment-rentals";
  readonly attachmentMachineTypes = liveAttachmentMachineTypes(ATTACHMENT_MACHINE_TYPES, environment);
  selectedAttachmentMachine = this.attachmentMachineTypes[0] || ATTACHMENT_MACHINE_TYPES[0];
  attachmentTypeListed(handle: string): boolean {
    return this.attachmentMachineTypes.some(machine => machine.handle === handle);
  }
  selectedAttachmentCategory: AttachmentMenuCategory | null = null;
  attachmentCategories: AttachmentMenuCategory[] = [];
  attachmentCategoriesByMachine: Record<string, AttachmentMenuCategory[]> = {};
  attachmentCategoriesLoading = false;
  attachmentCategoriesError = false;
  private attachmentCategoriesRequest: Subscription;
  private attachmentCategoryRequest: Subscription;
  private readonly attachmentEndpoint = (environment.commerceProductsApiDomain ?? environment.assetsApiDomain) + "/api/assets/v2/commerce-products/public";

  readonly departments = COMMERCE_DEPARTMENTS;
  readonly departmentNavigationEnabled = false;
  readonly partsNavigationEnabled = false;
  readonly storefrontEnabled = isStorefrontEnabled(environment);
  selectedDepartment = COMMERCE_DEPARTMENTS[0];
  departmentCategories: AttachmentMenuCategory[] = [];
  private departmentRequest: Subscription;
  get selectedDepartmentCategories() { return this.departmentCategories.filter(category => category.departmentHandle === this.selectedDepartment.handle); }
  openDropdown: number = -1;

  // Toggled by HeaderService — true only on home + search-results routes.
  showMobileDropdowns: boolean = false;

  // Rental pages keep the original click menus; only Shop pages use the hover mega menus.
  commerceNavigation = false;

  locationsData: LocationState[] = HEADER_LOCATIONS;

  constructor(
    private azureSearchService: AzureSearchService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private elementRef: ElementRef,
    private headerService: HeaderService,
    private renderer: Renderer2,
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.mobileDropdownsSubscription = this.headerService.showMobileLeftSideDropdowns.subscribe(enabled => {
      this.showMobileDropdowns = enabled;
      if (!enabled) {
        // Make sure no stale sheet stays open if we navigate to a page that hides mobile dropdowns.
        this.openDropdown = -1;
      }
      this.syncBodyChipClass(enabled && !this.commerceNavigation);
    });
  }

  private syncBodyChipClass(enabled: boolean) {
    if (!this.document || !this.document.body) {
      return;
    }
    if (enabled) {
      this.renderer.addClass(this.document.body, BODY_CLASS_WITH_CHIPS);
    } else {
      this.renderer.removeClass(this.document.body, BODY_CLASS_WITH_CHIPS);
    }
  }

  ngOnDestroy(): void {
    if (this.menuPositionFrame) cancelAnimationFrame(this.menuPositionFrame);
    clearTimeout(this.menuCloseTimer);
    window.visualViewport?.removeEventListener("resize", this.viewportResize);
    this.mobileDropdownsSubscription?.unsubscribe();
    this.subscription?.unsubscribe();
    this.attachmentCategoriesRequest?.unsubscribe();
    this.attachmentCategoryRequest?.unsubscribe();
    this.departmentRequest?.unsubscribe();
    // Clean up so the larger top margin doesn't leak if the component is torn down.
    this.syncBodyChipClass(false);
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.openDropdown = -1;
    }
  }

  ngOnInit(): void {
    window.visualViewport?.addEventListener("resize", this.viewportResize);
    this.setCommerceNavigation(this.router.url);
    this.loadAttachmentMenus();
    this.subscription = this.router.events.pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)).subscribe(event => {
      if (event instanceof NavigationEnd) this.setCommerceNavigation(event.urlAfterRedirects || event.url);
      else this.openDropdown = -1;
    });
  }

  categoriesFor(handle: string): AttachmentMenuCategory[] {
    return this.attachmentCategoriesByMachine[handle] || [];
  }

  private populatedCategories(categories: AttachmentMenuCategory[] | null | undefined): AttachmentMenuCategory[] {
    return (categories || []).filter(category => category.count > 0 && !!category.handle);
  }

  /** Load every live machine's categories up front so the menu links are in the HTML without a hover. */
  private loadAttachmentMenus(): void {
    if (!this.storefrontEnabled || !this.attachmentMachineTypes.length) return;
    this.attachmentCategoriesLoading = true;
    this.attachmentCategoriesError = false;
    const requests = this.attachmentMachineTypes.map(machine =>
      this.http.get<AttachmentMenuCategory[]>(this.attachmentEndpoint + "/categories", {
        params: new HttpParams().set("machineType", machine.handle),
      }).pipe(
        map(categories => ({ handle: machine.handle, categories: this.populatedCategories(categories), failed: false })),
        catchError(() => of({ handle: machine.handle, categories: [] as AttachmentMenuCategory[], failed: true })),
      ),
    );
    this.attachmentCategoriesRequest?.unsubscribe();
    this.attachmentCategoriesRequest = forkJoin(requests).subscribe(results => {
      this.attachmentCategoriesLoading = false;
      const selected = results.find(result => result.handle === this.selectedAttachmentMachine?.handle);
      this.attachmentCategoriesError = !!selected?.failed;
      for (const result of results) this.attachmentCategoriesByMachine[result.handle] = result.categories;
      this.applySelectedAttachmentCategories();
    });
  }

  private applySelectedAttachmentCategories(): void {
    this.attachmentCategories = this.categoriesFor(this.selectedAttachmentMachine?.handle);
    this.selectedAttachmentCategory = this.attachmentCategories[0] || null;
  }

  toggleDropdown(event: MouseEvent, index: number) {
    event.stopPropagation();
    if (this.openDropdown === index) {
      this.openDropdown = -1;
      return;
    }
    this.openMenu(index);
  }

  private setCommerceNavigation(url: string): void {
    this.commerceNavigation = isCommerceNavigationUrl(url);
    this.syncBodyChipClass(this.showMobileDropdowns && !this.commerceNavigation);
  }

  private menuOpensOnHover(index: number): boolean {
    return this.commerceNavigation || index === 4;
  }

  clickMenu(event: MouseEvent, index: number): void {
    if (this.menuOpensOnHover(index)) this.openMenu(index);
    else this.toggleDropdown(event, index);
  }

  hoverMenu(index: number): void { if (this.menuOpensOnHover(index)) this.openMenu(index); }
  hoverMenuEnd(index: number): void { if (this.menuOpensOnHover(index)) this.closeMenu(index); }
  focusMenu(index: number): void { if (this.menuOpensOnHover(index)) this.openMenu(index); }
  blurMenu(event: FocusEvent, index: number): void { if (this.menuOpensOnHover(index)) this.closeMenuOnBlur(event, index); }

  openMenu(index: number): void {
    clearTimeout(this.menuCloseTimer);
    if (this.openDropdown === index) return;
    this.openDropdown = index;
    this.scheduleMenuPosition();
    if (index < 3) this.search(true, index);
    if (index === 6 && !this.departmentCategories.length) {
      this.departmentRequest?.unsubscribe();
      this.departmentRequest = this.http.get<AttachmentMenuCategory[]>(this.attachmentEndpoint + "/categories").subscribe({
        next: categories => this.departmentCategories = categories.filter(category => category.count > 0).map(category => ({ ...category,
          departmentHandle: category.departmentHandle || commerceDepartment({ categoryHandle: category.handle })?.handle })),
        error: () => this.departmentCategories = [],
      });
    }
    if (index === 4 && !this.attachmentCategories.length) this.selectAttachmentMachine(this.selectedAttachmentMachine);
  }

  @HostListener("window:resize")
  @HostListener("window:orientationchange")
  @HostListener("window:rubbl-header-resize")
  scheduleMenuPosition(): void {
    if (this.menuPositionFrame) cancelAnimationFrame(this.menuPositionFrame);
    this.menuPositionFrame = requestAnimationFrame(() => {
      this.menuPositionFrame = 0;
      const root = this.elementRef.nativeElement as HTMLElement;
      const parent = root.querySelector(".nav-dropdown--open") as HTMLElement;
      const trigger = parent?.querySelector(".nav-dropdown__toggle") as HTMLElement;
      const menu = parent?.querySelector(".nav-dropdown__menu") as HTMLElement;
      if (!menu || !trigger || !trigger.getClientRects().length) return;
      const header = root.closest(".navbar");
      const triggerRect = trigger.getBoundingClientRect();
      const width = menu.classList.contains("nav-dropdown__menu--attachments-two-column") ? 760
        : menu.classList.contains("nav-dropdown__menu--attachments") ? 1100
        : menu.classList.contains("nav-dropdown__menu--wide") ? 750
        : menu.classList.contains("nav-dropdown__menu--compact") ? 280 : 650;
      const bounds = headerMenuBounds(document.documentElement.clientWidth, window.visualViewport?.height || window.innerHeight,
        header?.getBoundingClientRect().bottom || triggerRect.bottom,
        triggerRect.left, triggerRect.right, width,
        menu.classList.contains("nav-dropdown__menu--wide"), menu.classList.contains("nav-dropdown__menu--attachments"));
      for (const [property, value] of Object.entries({ left: bounds.left, top: bounds.top, width: bounds.width, "max-height": bounds.maxHeight })) {
        this.renderer.setStyle(menu, property, `${value}px`);
      }
      this.renderer.setStyle(menu, "right", "auto");
      this.renderer.setStyle(menu, "transform", "none");
      const arrow = menu.querySelector(".arrow-up");
      if (arrow) this.renderer.setStyle(arrow, "left", `${Math.max(12, Math.min(bounds.width - 24, triggerRect.left + triggerRect.width / 2 - bounds.left))}px`);
    });
  }

  selectAttachmentMachine(machine: typeof ATTACHMENT_MACHINE_TYPES[number]): void {
    if (!machine) return;
    this.selectedAttachmentMachine = machine;
    if (this.attachmentCategoriesByMachine[machine.handle]) {
      this.applySelectedAttachmentCategories();
      return;
    }
    this.attachmentCategories = [];
    this.selectedAttachmentCategory = null;
    this.attachmentCategoriesLoading = true;
    this.attachmentCategoriesError = false;
    this.attachmentCategoryRequest?.unsubscribe();
    this.attachmentCategoryRequest = this.http.get<AttachmentMenuCategory[]>(this.attachmentEndpoint + "/categories", {
      params: new HttpParams().set("machineType", machine.handle),
    }).subscribe({
      next: categories => {
        const populated = this.populatedCategories(categories);
        this.attachmentCategoriesByMachine[machine.handle] = populated;
        if (this.selectedAttachmentMachine.handle !== machine.handle) return;
        this.attachmentCategoriesLoading = false;
        this.applySelectedAttachmentCategories();
      },
      error: () => {
        this.attachmentCategoriesByMachine[machine.handle] = [];
        if (this.selectedAttachmentMachine.handle === machine.handle) {
          this.attachmentCategories = [];
          this.attachmentCategoriesLoading = false;
          this.attachmentCategoriesError = true;
        }
      },
    });
  }

  selectAttachmentCategory(category: AttachmentMenuCategory): void {
    this.selectedAttachmentCategory = category;
  }

  closeMenu(index: number): void {
    clearTimeout(this.menuCloseTimer);
    this.menuCloseTimer = setTimeout(() => {
      if (this.openDropdown === index) this.openDropdown = -1;
    }, 200);
  }

  closeMenuOnBlur(event: FocusEvent, index: number): void {
    const next = event.relatedTarget as Node | null;
    if (!next || !(event.currentTarget as HTMLElement).contains(next)) this.closeMenu(index);
  }

  /**
   * Close the mobile dropdown sheet when any link inside it is tapped (covers the
   * edge case where the link points to the current URL and NavigationStart never fires).
   * Stops propagation so the document:click handler doesn't fight us on internal clicks.
   */
  handleMobileSheetClick(event: MouseEvent) {
    event.stopPropagation();
    const target = event.target as HTMLElement | null;
    if (target && target.closest("a")) {
      this.openDropdown = -1;
    }
  }

  getLocationRoute(city: string, stateAbbr: string): string {
    return headerLocationRoute(city, stateAbbr);
  }

  search(open: boolean, indexForSearch: number) {
    if (!open) {
      return;
    }

    this.title = this.renterNavigationItem[indexForSearch].title;
    this.queryParams = this.renterNavigationItem[indexForSearch].queryParams;
    this.selectedRoute = this.renterNavigationItem[indexForSearch].route;

    const location = this.localStorageService.get("current-location");

    const azureSearchRequest = new AzureSearchRequest();
    azureSearchRequest.search = "*";
    azureSearchRequest.top = 0;

    // For "Buy it Now", don't require rentalRate (buyItNowOnly items may not have rental rates)
    let filter = this.baseFilter;
    if (indexForSearch === 1) {
      filter = filter.replace("(rentalRate ne 0) and ", "");
    }
    azureSearchRequest.filter = indexForSearch == 0 ? this.baseFilter : filter + this.renterNavigationItem[indexForSearch].filter;

    if (Number.isFinite(location?.longitude) && Number.isFinite(location?.latitude)) {
      azureSearchRequest.filter += ` and (geo.distance(location/point, geography'POINT(${location.longitude}
                 ${location.latitude})') le ${ANYWHERE_SEARCH_RADIUS_KM})`;
    }

    azureSearchRequest.searchFields = "primaryType, indexKey";
    azureSearchRequest.facets = ["primaryType,count:50", "indexKey, count:1000, sort:value"];

    const machineInventoryIndex$ = this.azureSearchService.search<InventoryItem>(azureSearchRequest, "machines");

    forkJoin([machineInventoryIndex$]).subscribe(result => {
      if (this.openDropdown !== indexForSearch) return;
      let inventoryPrimaryTypes = result[0].search.facets.primaryType;
      const data = [];

      inventoryPrimaryTypes.sort((a, b) => b.count - a.count);
      for (const inventoryPrimaryType of inventoryPrimaryTypes) {
        // With the storefront on, supplier attachments are reached through the ecommerce Attachments menu.
        if (this.storefrontEnabled && inventoryPrimaryType.value === ATTACHMENTS_PRIMARY_TYPE) {
          continue;
        }
        if (data.length < 26) {
          const dataItem = {
            header: `${inventoryPrimaryType.value}`,
            subHeader: `${inventoryPrimaryType.count}`,
            route: this.renterNavigationItem[indexForSearch].route,
            queryParams: this.renterNavigationItem[indexForSearch].queryParams,
          };

          data.push(dataItem);
        }
      }

      this.popularMachineCategoryData = data;
    });
  }

  machineCardParams(machineCardParams: any) {
    return {
      ...machineCardParams,
      ...this.queryParams,
    };
  }

  selectedRouteSegment() {
    return this.selectedRoute || "equipment-rentals";
  }
}
